import type Context from '@/context';
import {
  createRuntimeHandleScope,
  type NodeHandle,
  type EdgeHandle,
  type RuntimeHandleScope,
} from './RuntimeHandles';

/** Project ownership and an operation-local importer. No live string graph. */
export class RuntimeGraphIds {
  readonly scope: RuntimeHandleScope = createRuntimeHandleScope();
  readonly #prefix = `r:${crypto.randomUUID()}:`;

  /** Persisted identity component, never a live graph address on hydration. */
  get nodePrefix(): string {
    return this.#prefix;
  }

  nodeText(id: NodeHandle): string {
    return this.#prefix + id;
  }

  edgeText(id: EdgeHandle): string {
    return `${this.#prefix}e:${id}`;
  }

  importer(): RuntimeNodeImport {
    return new RuntimeNodeImport(this);
  }

  assertSame(other: RuntimeGraphIds): void {
    this.scope.assertSameScope(other.scope);
  }

  close(): void {
    this.scope.close();
  }
}

/** Discard after loading. Exact spellings live only on imported boundary records. */
export class RuntimeNodeImport {
  readonly #nodes = new Map<string, NodeHandle>();
  constructor(readonly ids: RuntimeGraphIds) {}
  node(externalId: string): NodeHandle {
    let id = this.#nodes.get(externalId);
    if (id === undefined) {
      id = this.ids.scope.allocateNode();
      this.#nodes.set(externalId, id);
    }
    return id;
  }
}

/** Standalone graphs share a scope; application graphs always use their context. */
export const standaloneRuntimeIds = new RuntimeGraphIds();

export function runtimeGraphIds(context: Context): RuntimeGraphIds {
  return (context.runtimeGraphIds ??= new RuntimeGraphIds());
}
